import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    FormControlLabel,
    IconButton,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import ApiBugReportCtrl from "../api/ApiBugReportCtrl";

/**
 * Dessine un cadre + label matérialisant le viewport réel de l'utilisateur
 * sur le canvas html2canvas (qui couvre tout le document).
 */
function drawViewportOverlay(canvas, viewport, scale) {
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const x = viewport.scrollX * scale;
    const y = viewport.scrollY * scale;
    const w = viewport.width * scale;
    const h = viewport.height * scale;

    ctx.save();
    // Cadre orange pointillé
    ctx.lineWidth = Math.max(2, 2 * scale);
    ctx.strokeStyle = "rgba(255, 87, 34, 0.95)";
    ctx.setLineDash([12 * scale, 8 * scale]);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);

    // Label en haut à gauche du viewport
    const fontSize = 14 * scale;
    ctx.font = `bold ${fontSize}px sans-serif`;
    const label = `Viewport ${viewport.width}×${viewport.height} · scroll (${viewport.scrollX}, ${viewport.scrollY}) · DPR ${window.devicePixelRatio}`;
    const padding = 6 * scale;
    const labelW = ctx.measureText(label).width + padding * 2;
    const labelH = fontSize + padding * 2;
    const labelX = x + 4 * scale;
    const labelY = y + 4 * scale;

    ctx.fillStyle = "rgba(255, 87, 34, 0.95)";
    ctx.fillRect(labelX, labelY, labelW, labelH);
    ctx.fillStyle = "#fff";
    ctx.textBaseline = "top";
    ctx.fillText(label, labelX + padding, labelY + padding);
    ctx.restore();
}

/**
 * Capture la page courante avec html2canvas (import dynamique) et
 * superpose un overlay matérialisant le viewport réel de l'écran.
 * Retourne la data-URL PNG ou null en cas d'échec.
 */
async function capturePageScreenshot() {
    try {
        const html2canvas = (await import("html2canvas")).default;
        const scale = Math.min(window.devicePixelRatio, 2);
        const viewport = {
            scrollX: window.scrollX,
            scrollY: window.scrollY,
            width: window.innerWidth,
            height: window.innerHeight,
        };
        const canvas = await html2canvas(document.body, {
            useCORS: true,
            allowTaint: true,
            logging: false,
            scale,
        });
        drawViewportOverlay(canvas, viewport, scale);
        return canvas.toDataURL("image/png");
    } catch (e) {
        console.error("[BugReport] Échec de la capture écran :", e);
        return null;
    }
}

/**
 * Bouton flottant (FAB) affiché en bas à droite pour signaler un problème.
 * La capture d'écran est prise AVANT l'ouverture du dialogue afin de refléter
 * l'état réel de la page. Elle est ensuite uploadée sur la plateforme si l'utilisateur
 * l'autorise, et son URL publique est jointe au rapport Discord.
 */
export default function BugReportFab() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { showToast } = useToast();
    const location = useLocation();

    const [open, setOpen] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const [sending, setSending] = useState(false);

    // Nature du signalement : "BUG" ou "FEATURE"
    const [kind, setKind] = useState("BUG");
    const isFeature = kind === "FEATURE";

    // Données du formulaire
    const [description, setDescription] = useState("");
    const [details, setDetails] = useState("");
    const [pageStatus, setPageStatus] = useState("");
    const [includeScreenshot, setIncludeScreenshot] = useState(true);
    const [screenshotDataUrl, setScreenshotDataUrl] = useState(null);
    const [descTouched, setDescTouched] = useState(false);

    // Informations de contact (pré-remplies depuis le profil)
    const [contactFirstName, setContactFirstName] = useState(user?.firstName ?? "");
    const [contactLastName, setContactLastName] = useState(user?.lastName ?? "");
    const [contactEmail, setContactEmail] = useState(user?.email ?? "");

    // ── Ouverture : capture d'abord, dialogue ensuite ────────────────────────
    const handleOpen = async () => {
        setCapturing(true);
        const dataUrl = await capturePageScreenshot();
        setScreenshotDataUrl(dataUrl);
        setCapturing(false);
        setOpen(true);
    };

    // ── Fermeture / reset ────────────────────────────────────────────────────
    const handleClose = () => {
        if (sending) return;
        setOpen(false);
        setKind("BUG");
        setDescription("");
        setDetails("");
        setPageStatus("");
        setIncludeScreenshot(true);
        setScreenshotDataUrl(null);
        setDescTouched(false);
        setContactFirstName(user?.firstName ?? "");
        setContactLastName(user?.lastName ?? "");
        setContactEmail(user?.email ?? "");
    };

    // ── Envoi ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        setDescTouched(true);
        if (!description.trim()) return;

        setSending(true);

        try {
            // 1. Extraction du base64 brut (suppression du préfixe data:image/png;base64,)
            let screenshotBase64;
            if (includeScreenshot && screenshotDataUrl) {
                const comma = screenshotDataUrl.indexOf(",");
                screenshotBase64 = comma >= 0 ? screenshotDataUrl.slice(comma + 1) : screenshotDataUrl;
            }

            // 2. Envoi du rapport
            const { error } = await ApiBugReportCtrl.sendBugReport({
                kind,
                description: description.trim(),
                details: details.trim() || undefined,
                path: location.pathname,
                username:
                    user?.login ?? user?.email ?? (contactEmail.trim() || "anonyme"),
                pageStatus:
                    pageStatus.trim() ||
                    `${window.location.pathname}${window.location.search}`,
                screenshotBase64,
                contactFirstName: contactFirstName.trim() || undefined,
                contactLastName: contactLastName.trim() || undefined,
                contactEmail: contactEmail.trim() || undefined,
            });

            if (error) {
                showToast({
                    msg: t(
                        "bugReport.error",
                        "Impossible d'envoyer le rapport. Veuillez réessayer."
                    ),
                    severity: "error",
                });
            } else {
                showToast({
                    msg: t(
                        "bugReport.success",
                        "Rapport envoyé ! Merci pour votre retour."
                    ),
                    severity: "success",
                });
                handleClose();
            }
        } catch (e) {
            console.error("[BugReport] Erreur envoi :", e);
            showToast({
                msg: t("bugReport.error", "Impossible d'envoyer le rapport."),
                severity: "error",
            });
        } finally {
            setSending(false);
        }
    };


    return (
        <>
            {/* ── FAB ─────────────────────────────────────────────────────── */}
            <Tooltip
                title={t("bugReport.fab", "Signaler un problème")}
                placement="left"
            >
                <span
                    style={{
                        position: "fixed",
                        bottom: 24,
                        right: 24,
                        zIndex: 1300,
                    }}
                >
                    <Fab
                        size="small"
                        color="warning"
                        onClick={handleOpen}
                        disabled={capturing}
                        aria-label={t("bugReport.fab", "Signaler un problème")}
                    >
                        {capturing ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <BugReportIcon />
                        )}
                    </Fab>
                </span>
            </Tooltip>

            {/* ── Dialogue ──────────────────────────────────────────────────────── */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                disableScrollLock
            >
                <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {isFeature
                            ? <LightbulbIcon color="success" />
                            : <BugReportIcon color="warning" />}
                        <Typography variant="h6" fontWeight={600}>
                            {isFeature
                                ? t("bugReport.titleFeature", "Proposer une fonctionnalité")
                                : t("bugReport.title", "Signaler un problème")}
                        </Typography>
                    </Box>
                    <IconButton size="small" onClick={handleClose} disabled={sending}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
                    {/* Type de signalement : Bug / Fonctionnalité */}
                    <ToggleButtonGroup
                        value={kind}
                        exclusive
                        fullWidth
                        size="small"
                        onChange={(_e, v) => v && setKind(v)}
                        aria-label={t("bugReport.kind", "Type de signalement")}
                    >
                        <ToggleButton value="BUG" color="warning">
                            <BugReportIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {t("bugReport.kindBug", "Bug")}
                        </ToggleButton>
                        <ToggleButton value="FEATURE" color="success">
                            <LightbulbIcon fontSize="small" sx={{ mr: 0.5 }} />
                            {t("bugReport.kindFeature", "Fonctionnalité")}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* Description */}
                    <TextField
                        label={isFeature
                            ? t("bugReport.descriptionFeature", "Fonctionnalité souhaitée")
                            : t("bugReport.description", "Description")}
                        multiline
                        rows={3}
                        size="small"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        onBlur={() => setDescTouched(true)}
                        required
                        error={descTouched && !description.trim()}
                        helperText={descTouched && !description.trim()
                            ? t("bugReport.descriptionRequired", "Obligatoire")
                            : ""}
                        fullWidth
                        autoFocus
                        placeholder={isFeature
                            ? t("bugReport.descriptionFeaturePlaceholder", "Décrivez la fonctionnalité souhaitée…")
                            : t("bugReport.descriptionPlaceholder", "Décrivez le problème…")}
                    />

                    {/* Détails */}
                    <TextField
                        label={t("bugReport.details", "Détails (optionnel)")}
                        multiline
                        rows={2}
                        size="small"
                        value={details}
                        onChange={(e) => setDetails(e.target.value)}
                        fullWidth
                        placeholder={t("bugReport.detailsPlaceholder", "Message d'erreur, étapes…")}
                    />

                    {/* Statut */}
                    <TextField
                        label={t("bugReport.pageStatus", "Contexte (optionnel)")}
                        size="small"
                        value={pageStatus}
                        onChange={(e) => setPageStatus(e.target.value)}
                        fullWidth
                        placeholder={t("bugReport.pageStatusPlaceholder", "ex : erreur 500…")}
                    />

                    {/* Contact */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <TextField
                            label={t("bugReport.firstName", "Prénom")}
                            size="small"
                            value={contactFirstName}
                            onChange={(e) => setContactFirstName(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label={t("bugReport.lastName", "Nom")}
                            size="small"
                            value={contactLastName}
                            onChange={(e) => setContactLastName(e.target.value)}
                            fullWidth
                        />
                    </Box>
                    <TextField
                        label={t("bugReport.email", "Email")}
                        type="email"
                        size="small"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        fullWidth
                    />

                    {/* Screenshot */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={includeScreenshot}
                                    onChange={(e) => setIncludeScreenshot(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    {t("bugReport.includeScreenshot", "Inclure une capture d'écran")}
                                </Typography>
                            }
                        />
                        {includeScreenshot && screenshotDataUrl && (
                            <Box
                                component="img"
                                src={screenshotDataUrl}
                                alt="screenshot preview"
                                sx={{
                                    width: "100%",
                                    maxHeight: 120,
                                    objectFit: "contain",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    mt: 0.5,
                                }}
                            />
                        )}
                        {includeScreenshot && !screenshotDataUrl && (
                            <Alert severity="warning" sx={{ mt: 0.5 }} icon={false}>
                                <Typography variant="caption">
                                    {t("bugReport.screenshotFailed", "Capture indisponible.")}
                                </Typography>
                            </Alert>
                        )}
                    </Box>
                    </Box>
                </DialogContent>

                <Divider />

                {/* Actions */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                    <Button size="small" onClick={handleClose} disabled={sending}>
                        {t("common.cancel", "Annuler")}
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color={isFeature ? "success" : "warning"}
                        onClick={handleSubmit}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={14} color="inherit" /> : null}
                    >
                        {sending ? t("bugReport.sending", "Envoi…") : t("bugReport.send", "Envoyer")}
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}

